import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy, where, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { saveUserAutomationStatus, saveUserAutomationCustomizations } from '../../services/userAutomationService';
import Modal from '../../components/Modal';
import {
  ArrowLeft,
  Zap,
  Trash2,
  Edit,
  Check,
  X,
  Plus,
  ChevronUp,
  ChevronDown,
  SmilePlus,
  CheckCircle,
  Clock,
  AlertTriangle,
  Eye,
  User,
  Calendar,
  Building2,
  Tag,
} from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

/**
 * CRM fulfillment view of a single automation request. Same design language as
 * AutomationTemplateOverview (display emoji, title+emoji workflow steps), plus
 * the fulfillment-only pieces: status management, request info, software
 * connection state, and delete.
 */

const STATUS_OPTIONS = ['requested', 'needs-approval', 'approved', 'in-progress', 'on', 'off', 'hidden'];

const AutomationDetail = () => {
  const { darkMode } = useTheme();
  const { automationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableSoftwares, setAvailableSoftwares] = useState([]);
  const [userCustomizations, setUserCustomizations] = useState({});
  const [userSoftwareConnections, setUserSoftwareConnections] = useState({});
  const [userTechStack, setUserTechStack] = useState({});
  const [requestingUserId, setRequestingUserId] = useState(null);
  const [accountId, setAccountId] = useState(() => location.state?.accountId ?? null);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingConnectionStatus, setSavingConnectionStatus] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    category: 'general',
    softwares: [],
    workflowSteps: [],
  });
  const [newStepTitle, setNewStepTitle] = useState('');
  const [newSoftware, setNewSoftware] = useState('');

  // Emoji picker — targets the display emoji ({ kind: 'display' }) or a step ({ kind: 'step', index })
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [emojiSaving, setEmojiSaving] = useState(false);

  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const panelCard = `bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`;
  const inputClass = `w-full rounded-md border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  // Fetch available softwares (types + individual softwares) from technologyStack
  useEffect(() => {
    const fetchSoftwares = async () => {
      try {
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        const softwareList = [];

        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));

          for (const typeDoc of typesSnapshot.docs) {
            const typeData = typeDoc.data();
            if (typeData && typeData.name) {
              softwareList.push({
                id: typeDoc.id,
                name: typeData.name,
                tier: collectionName,
                description: typeData.description || '',
                image: typeData.image || '',
                isType: true,
              });

              const softwareSnapshot = await getDocs(
                collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares')
              );
              softwareSnapshot.forEach((softwareDoc) => {
                const data = softwareDoc.data();
                if (data && data.name) {
                  softwareList.push({
                    id: softwareDoc.id,
                    name: data.name,
                    type: typeDoc.id,
                    typeName: typeData.name,
                    tier: collectionName,
                    description: data.description || '',
                    image: data.image || '',
                  });
                }
              });
            }
          }
        }

        softwareList.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSoftwares(softwareList);
      } catch (error) {
        console.error('Error fetching software list:', error);
      }
    };

    fetchSoftwares();
  }, []);

  // Get accountId from location state or find it from user data
  useEffect(() => {
    const findAccountId = async () => {
      if (location.state?.accountId) {
        setAccountId(location.state.accountId);
        return;
      }

      // Not in location state (e.g. opened by direct link) — derive from the requesting user
      if (requestingUserId) {
        try {
          const userDoc = await getDoc(doc(db, 'users', requestingUserId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.accountId) {
              setAccountId(userData.accountId);
            } else if (userData.email) {
              const accountsQuery = query(
                collection(db, 'accounts'),
                where('contact.email', '==', userData.email)
              );
              const accountsSnapshot = await getDocs(accountsQuery);
              if (!accountsSnapshot.empty) {
                setAccountId(accountsSnapshot.docs[0].id);
              }
            }
          }
        } catch (error) {
          console.error('Error finding accountId from userId:', error);
        }
      }
    };

    findAccountId();
  }, [location.state, requestingUserId]);

  const mapRequestToAutomation = (request) => ({
    id: request.id,
    name: request.name,
    description: request.description,
    softwares: request.softwares || [],
    emojis: request.emojis || [],
    workflowSteps: request.workflowSteps || [],
    category: request.category || 'general',
    status: request.status || 'requested',
    requestedBy: request.requestedBy || 'Unknown User',
    company: request.company || 'Unknown Company',
    requestDate: request.createdAt || new Date().toISOString(),
    lastUpdated: request.lastUpdated || null,
    statusHistory: request.statusHistory || [],
    requestedByName: request.requestedBy || 'Unknown User',
    isUserRequest: true,
  });

  // Fetch automation details — account request array is the source of truth for
  // user-created requests; the automations collection holds templates.
  useEffect(() => {
    const effectiveAccountId = accountId ?? location.state?.accountId ?? null;

    const fetchAutomation = async () => {
      try {
        // Check the account's userAutomationRequests first
        if (effectiveAccountId) {
          try {
            const accountDoc = await getDoc(doc(db, 'accounts', effectiveAccountId));
            if (accountDoc.exists()) {
              const accountData = accountDoc.data();
              const accountRequest = (accountData.userAutomationRequests || []).find(
                (req) => req.id === automationId
              );

              if (accountRequest) {
                let foundUserId = accountRequest.userId || null;
                if (!foundUserId) {
                  const accountEmail = accountData.contact?.email || accountData.email;
                  if (accountEmail) {
                    const usersSnapshot = await getDocs(
                      query(collection(db, 'users'), where('email', '==', accountEmail))
                    );
                    if (!usersSnapshot.empty) foundUserId = usersSnapshot.docs[0].id;
                  }
                }

                setAutomation(mapRequestToAutomation(accountRequest));
                setRequestingUserId(foundUserId);
                setSelectedStatus(accountRequest.status || 'requested');
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.error('Error checking account for automation:', error);
          }
        }

        // If accountId is provided, find the user associated with that account
        let accountUserId = null;
        if (effectiveAccountId) {
          try {
            const accountDoc = await getDoc(doc(db, 'accounts', effectiveAccountId));
            if (accountDoc.exists()) {
              const accountData = accountDoc.data();
              const accountEmail = accountData.contact?.email || accountData.email;
              if (accountEmail) {
                const usersSnapshot = await getDocs(
                  query(collection(db, 'users'), where('email', '==', accountEmail))
                );
                if (!usersSnapshot.empty) accountUserId = usersSnapshot.docs[0].id;
              }
            }
          } catch (error) {
            console.error('Error finding user for account:', error);
          }
        }

        // Try it as a template automation
        const automationDoc = await getDoc(doc(db, 'automations', automationId));

        if (automationDoc.exists()) {
          const data = automationDoc.data();

          let requestedByName = 'Unknown';
          let company = 'Unknown Company';
          let requestDate = new Date().toISOString();
          let userId = accountUserId;

          if (!accountUserId) {
            try {
              // Find the request that points at this template
              const requestsSnapshot = await getDocs(
                query(collection(db, 'automationRequests'), where('automationId', '==', automationId))
              );
              if (!requestsSnapshot.empty) {
                const requestData = requestsSnapshot.docs[0].data();
                requestedByName = requestData.requestedBy || 'Unknown';
                company = requestData.company || 'Unknown Company';
                userId = requestData.userId || null;
                requestDate = requestData.createdAt
                  ? (requestData.createdAt.toDate ? requestData.createdAt.toDate().toISOString() : requestData.createdAt)
                  : new Date().toISOString();
              }
            } catch (error) {
              console.error('Error fetching automation request data:', error);
            }
          } else {
            try {
              const accountDoc = await getDoc(doc(db, 'accounts', effectiveAccountId));
              if (accountDoc.exists()) {
                const accountData = accountDoc.data();
                const contact = accountData.contact || {};
                requestedByName =
                  contact.name ||
                  (contact.firstName && contact.lastName
                    ? `${contact.firstName} ${contact.lastName}`
                    : contact.firstName || contact.lastName || 'Unknown');
                company = accountData.company || 'Unknown Company';
              }
            } catch (error) {
              console.error('Error fetching account data:', error);
            }
          }

          // Prefer workflow steps from the doc; fall back to the subcollection
          let workflowSteps = [];
          if (Array.isArray(data.workflowSteps) && data.workflowSteps.length > 0) {
            workflowSteps = [...data.workflowSteps].sort((a, b) => (a.order || 0) - (b.order || 0));
          } else {
            try {
              const stepsSnapshot = await getDocs(
                query(collection(db, `automations/${automationId}/workflowSteps`), orderBy('order', 'asc'))
              );
              workflowSteps = stepsSnapshot.docs.map((stepDoc) => ({ id: stepDoc.id, ...stepDoc.data() }));
            } catch (error) {
              console.error('Error fetching workflow steps:', error);
              workflowSteps = data.workflowSteps || [];
            }
          }

          setAutomation({
            id: automationDoc.id,
            ...data,
            status: 'requested', // updated from user data below
            requestDate,
            requestedByName,
            company,
            workflowSteps,
            isTemplate: true,
          });
          setRequestingUserId(userId);
          setSelectedStatus('requested');
        } else {
          // User-created automation request — check account user, then all users
          let foundAutomation = null;
          let foundUserId = null;

          if (accountUserId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', accountUserId));
              if (userDoc.exists()) {
                const userRequest = (userDoc.data().userAutomationRequests || []).find(
                  (req) => req.id === automationId
                );
                if (userRequest) {
                  foundAutomation = mapRequestToAutomation(userRequest);
                  foundUserId = accountUserId;
                }
              }
            } catch (error) {
              console.error('Error checking account user for automation:', error);
            }
          }

          if (!foundAutomation && !accountUserId) {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            for (const userDoc of usersSnapshot.docs) {
              const userRequest = (userDoc.data().userAutomationRequests || []).find(
                (req) => req.id === automationId
              );
              if (userRequest) {
                foundAutomation = mapRequestToAutomation(userRequest);
                foundUserId = userDoc.id;
                break;
              }
            }
          }

          if (foundAutomation) {
            setAutomation(foundAutomation);
            setRequestingUserId(foundUserId);
            setSelectedStatus(foundAutomation.status);
          } else {
            console.error('Automation not found after searching all sources');
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching automation:', error);
        setLoading(false);
      }
    };

    if (automationId) {
      fetchAutomation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationId, accountId, location.state?.accountId]);

  // Track if we just saved status to prevent override
  const justSavedStatusRef = React.useRef(false);
  // Track if we've already loaded initial data to prevent loops
  const hasLoadedInitialDataRef = React.useRef(false);
  // Track if this is a user request to avoid dependency on automation object
  const isUserRequestRef = React.useRef(false);
  // Track the last requestingUserId to reset flags when user changes
  const lastRequestingUserIdRef = React.useRef(null);

  useEffect(() => {
    if (automation) {
      isUserRequestRef.current = automation.isUserRequest || false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automation?.isUserRequest]);

  useEffect(() => {
    if (requestingUserId !== lastRequestingUserIdRef.current) {
      hasLoadedInitialDataRef.current = false;
      lastRequestingUserIdRef.current = requestingUserId;
    }
  }, [requestingUserId]);

  // Fetch the requesting user's customizations, status, connections, and tech stack
  useEffect(() => {
    const fetchUserData = async () => {
      if (!requestingUserId || !automationId || !automation) return;

      // If we just saved status, skip this fetch to prevent override
      if (justSavedStatusRef.current) {
        setTimeout(() => {
          justSavedStatusRef.current = false;
        }, 2000);
        return;
      }

      try {
        // For user-created requests the account document is the source of truth for status
        if (isUserRequestRef.current && accountId) {
          try {
            const accountDoc = await getDoc(doc(db, 'accounts', accountId));
            if (accountDoc.exists()) {
              const accountRequest = (accountDoc.data().userAutomationRequests || []).find(
                (req) => req.id === automationId
              );
              if (accountRequest) {
                setAutomation((prev) => {
                  const accountStatus = accountRequest.status;
                  const accountHistory = accountRequest.statusHistory || [];
                  const statusChanged = prev.status !== accountStatus;
                  const historyLonger = accountHistory.length > (prev.statusHistory || []).length;
                  if (statusChanged || historyLonger) {
                    return {
                      ...prev,
                      ...(accountStatus != null && { status: accountStatus }),
                      ...(accountHistory.length > 0 && { statusHistory: accountHistory }),
                    };
                  }
                  return prev;
                });
                setSelectedStatus((prevStatus) =>
                  prevStatus !== accountRequest.status ? accountRequest.status : prevStatus
                );

                if (accountRequest.customizations) {
                  setUserCustomizations(accountRequest.customizations);
                  if (accountRequest.customizations.workflowSteps) {
                    setAutomation((prev) => {
                      const currentSteps = JSON.stringify(prev.workflowSteps || []);
                      const newSteps = JSON.stringify(accountRequest.customizations.workflowSteps);
                      return currentSteps !== newSteps
                        ? { ...prev, workflowSteps: accountRequest.customizations.workflowSteps }
                        : prev;
                    });
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error checking account document for status:', error);
          }
        }

        // For user-created requests without an account match, also read the user's copy
        if (isUserRequestRef.current) {
          const userDoc = await getDoc(doc(db, 'users', requestingUserId));
          if (userDoc.exists()) {
            const userRequest = (userDoc.data().userAutomationRequests || []).find(
              (req) => req.id === automationId
            );
            if (userRequest?.customizations) {
              setUserCustomizations((prev) =>
                Object.keys(prev).length === 0 ? userRequest.customizations : prev
              );
            }
          }
        }

        // For template automations, status and customizations live on userAutomations
        if (!isUserRequestRef.current) {
          const userDoc = await getDoc(doc(db, 'users', requestingUserId));
          let userAutomationData = { status: 'off', customizations: {} };
          if (userDoc.exists()) {
            const userAutomations = userDoc.data().userAutomations || {};
            userAutomationData = userAutomations[automationId] || { status: 'off', customizations: {} };
          }

          setUserCustomizations(userAutomationData.customizations || {});

          if (userAutomationData.customizations?.workflowSteps) {
            setAutomation((prev) => {
              const currentSteps = JSON.stringify(prev.workflowSteps || []);
              const newSteps = JSON.stringify(userAutomationData.customizations.workflowSteps);
              return currentSteps !== newSteps
                ? { ...prev, workflowSteps: userAutomationData.customizations.workflowSteps }
                : prev;
            });
          }

          if (userAutomationData.status) {
            setAutomation((prev) =>
              prev.status !== userAutomationData.status ? { ...prev, status: userAutomationData.status } : prev
            );
            setSelectedStatus((prevStatus) =>
              prevStatus !== userAutomationData.status ? userAutomationData.status : prevStatus
            );
          }
        }

        // Software connections and tech stack (once per user)
        if (!hasLoadedInitialDataRef.current) {
          const userDoc = await getDoc(doc(db, 'users', requestingUserId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserSoftwareConnections(userData.softwareConnections || {});
            setUserTechStack(userData.techStack || {});
          }
          hasLoadedInitialDataRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestingUserId, automationId, accountId]);

  // ---- Status display config (shared by badge and activity label) ----
  const statusConfig = {
    'requested': { bg: 'bg-yellow-100 text-yellow-800', icon: <AlertTriangle className="w-3 h-3" />, text: 'Requested' },
    'needs-approval': { bg: 'bg-amber-100 text-amber-800', icon: <AlertTriangle className="w-3 h-3" />, text: 'Needs Approval' },
    'approved': { bg: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" />, text: 'Approved' },
    'in-progress': { bg: 'bg-blue-100 text-blue-800', icon: <Clock className="w-3 h-3" />, text: 'In Progress' },
    'on': { bg: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" />, text: 'On' },
    'off': { bg: 'bg-gray-100 text-gray-800', icon: <Clock className="w-3 h-3" />, text: 'Off' },
    'hidden': { bg: 'bg-purple-100 text-purple-800', icon: <Eye className="w-3 h-3" />, text: 'Hidden' },
  };

  const getStatusLabel = (status) => (statusConfig[status] || statusConfig['requested']).text;

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig['requested'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
        {config.icon}
        <span className="ml-1">{config.text}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ---- Software display helpers ----
  const getSoftwareDisplayName = (softwareTypeId) => {
    const software = availableSoftwares.find((s) => s.id === softwareTypeId);
    if (software) return software.name;

    const userSelectedSoftware = userTechStack.selectedSoftware?.find((sw) => sw.toolId === softwareTypeId);
    if (userSelectedSoftware && userSelectedSoftware.name !== 'None') {
      return userSelectedSoftware.name;
    }

    return softwareTypeId;
  };

  const getUserSoftwareLogo = (softwareTypeId) => {
    const userSelectedSoftware = userTechStack.selectedSoftware?.find((sw) => sw.toolId === softwareTypeId);
    if (userSelectedSoftware && userSelectedSoftware.name !== 'None') {
      const userSoftware = availableSoftwares.find((s) => s.name === userSelectedSoftware.name);
      if (userSoftware?.image) return userSoftware.image;
    }
    const software = availableSoftwares.find((s) => s.id === softwareTypeId);
    return software?.image || null;
  };

  const isSoftwareConnected = (softwareTypeId) => userSoftwareConnections[softwareTypeId]?.connected || false;

  // Mark a software connection connected/disconnected on behalf of the client
  const handleUpdateConnectionStatus = async (softwareTypeId, connected) => {
    if (!requestingUserId) return;
    setSavingConnectionStatus(true);
    try {
      const userRef = doc(db, 'users', requestingUserId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        alert('User not found.');
        return;
      }
      const currentConnections = userDoc.data().softwareConnections || {};
      const existing = currentConnections[softwareTypeId] || {};
      const updatedConnections = {
        ...currentConnections,
        [softwareTypeId]: {
          ...existing,
          connected: !!connected,
          connectedAt: connected ? (existing.connectedAt || new Date().toISOString()) : null,
          connectionData: existing.connectionData || {},
        },
      };
      await updateDoc(userRef, {
        softwareConnections: updatedConnections,
        lastUpdated: new Date().toISOString(),
      });
      setUserSoftwareConnections(updatedConnections);
    } catch (error) {
      console.error('Error updating connection status:', error);
      alert('Failed to update connection status. Please try again.');
    } finally {
      setSavingConnectionStatus(false);
    }
  };

  // ---- Shared persistence helpers ----
  const resolveAccountId = async () => {
    if (accountId) return accountId;
    if (!requestingUserId) return null;
    try {
      const userDoc = await getDoc(doc(db, 'users', requestingUserId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.accountId) return userData.accountId;
        if (userData.email) {
          const accountsSnapshot = await getDocs(
            query(collection(db, 'accounts'), where('contact.email', '==', userData.email))
          );
          if (!accountsSnapshot.empty) return accountsSnapshot.docs[0].id;
        }
      }
    } catch (error) {
      console.error('Error finding accountId:', error);
    }
    return null;
  };

  // Apply a partial update to this request in both the user and account documents
  const patchUserRequestDocs = async (patch) => {
    const now = new Date().toISOString();
    const applyPatch = (requests) =>
      requests.map((req) => (req.id === automationId ? { ...req, ...patch, lastUpdated: now } : req));

    if (requestingUserId) {
      const userRef = doc(db, 'users', requestingUserId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        await updateDoc(userRef, {
          userAutomationRequests: applyPatch(userDoc.data().userAutomationRequests || []),
          lastUpdated: now,
        });
      }
    }

    const finalAccountId = await resolveAccountId();
    if (finalAccountId) {
      const accountRef = doc(db, 'accounts', finalAccountId);
      const accountDoc = await getDoc(accountRef);
      if (accountDoc.exists()) {
        await updateDoc(accountRef, {
          userAutomationRequests: applyPatch(accountDoc.data().userAutomationRequests || []),
          lastUpdated: now,
        });
      }
    }
  };

  // ---- Status save ----
  const handleSaveStatus = async () => {
    if (!automation || savingStatus || !selectedStatus || !requestingUserId) return;

    setSavingStatus(true);
    try {
      const now = new Date().toISOString();
      const historyEntry = { status: selectedStatus, timestamp: now };

      if (automation.isUserRequest) {
        // Update the user's copy plus the userAutomations mirror
        const userRef = doc(db, 'users', requestingUserId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const updatedRequests = (userData.userAutomationRequests || []).map((req) =>
            req.id === automationId
              ? { ...req, status: selectedStatus, lastUpdated: now, statusHistory: [...(req.statusHistory || []), historyEntry] }
              : req
          );
          const userAutomations = userData.userAutomations || {};
          const currentRequest = updatedRequests.find((req) => req.id === automationId);
          userAutomations[automationId] = {
            ...userAutomations[automationId],
            status: selectedStatus,
            lastUpdated: now,
            customizations: currentRequest?.workflowSteps
              ? { workflowSteps: currentRequest.workflowSteps }
              : (userAutomations[automationId]?.customizations || {}),
          };
          await updateDoc(userRef, {
            userAutomationRequests: updatedRequests,
            userAutomations,
            lastUpdated: now,
          });
        }

        // Update the account's copy (source of truth for PortalView)
        const finalAccountId = await resolveAccountId();
        if (finalAccountId) {
          const accountRef = doc(db, 'accounts', finalAccountId);
          const accountDoc = await getDoc(accountRef);
          if (accountDoc.exists()) {
            const accountRequests = accountDoc.data().userAutomationRequests || [];
            const exists = accountRequests.some((req) => req.id === automationId);
            const updatedAccountRequests = exists
              ? accountRequests.map((req) =>
                  req.id === automationId
                    ? { ...req, status: selectedStatus, lastUpdated: now, statusHistory: [...(req.statusHistory || []), historyEntry] }
                    : req
                )
              : [
                  ...accountRequests,
                  {
                    id: automationId,
                    name: automation.name,
                    description: automation.description,
                    category: automation.category,
                    softwares: automation.softwares || [],
                    emojis: automation.emojis || [],
                    workflowSteps: automation.workflowSteps || [],
                    status: selectedStatus,
                    requestedBy: automation.requestedBy || automation.requestedByName,
                    company: automation.company,
                    userId: requestingUserId,
                    createdAt: automation.requestDate || now,
                    lastUpdated: now,
                    statusHistory: [historyEntry],
                  },
                ];
            await updateDoc(accountRef, {
              userAutomationRequests: updatedAccountRequests,
              lastUpdated: now,
            });
          }
        }
      } else {
        // Template automation — the service writes user + account state
        await saveUserAutomationStatus(requestingUserId, automationId, selectedStatus);

        if (accountId) {
          try {
            const accountRef = doc(db, 'accounts', accountId);
            const accountDoc = await getDoc(accountRef);
            if (accountDoc.exists()) {
              const accountRequests = accountDoc.data().userAutomationRequests || [];
              const hasRequest = accountRequests.some(
                (req) => req.automationId === automationId || req.id === automationId
              );
              if (hasRequest) {
                const updatedAccountRequests = accountRequests.map((req) =>
                  req.automationId === automationId || req.id === automationId
                    ? { ...req, status: selectedStatus, lastUpdated: now, statusHistory: [...(req.statusHistory || []), historyEntry] }
                    : req
                );
                await updateDoc(accountRef, {
                  userAutomationRequests: updatedAccountRequests,
                  lastUpdated: now,
                });
              }
            }
          } catch (error) {
            console.error('Error updating account document for template automation:', error);
          }
        }
      }

      setAutomation((prev) => ({
        ...prev,
        status: selectedStatus,
        lastUpdated: now,
        statusHistory: [...(prev.statusHistory || []), historyEntry],
      }));

      // Prevent fetchUserData from overriding the fresh status
      justSavedStatusRef.current = true;
      setTimeout(() => {
        justSavedStatusRef.current = false;
      }, 3000);
    } catch (error) {
      console.error('Error updating automation status:', error);
      alert('Failed to update automation status. Please try again.');
    } finally {
      setSavingStatus(false);
    }
  };

  // ---- Details editing (name, description, category, softwares, workflow steps) ----
  const getWorkflowSteps = () => {
    const steps =
      userCustomizations?.workflowSteps && userCustomizations.workflowSteps.length > 0
        ? userCustomizations.workflowSteps
        : automation?.workflowSteps || [];
    return [...steps].sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const startEditing = () => {
    setEditForm({
      name: automation.name || '',
      description: automation.description || '',
      category: automation.category || 'general',
      softwares: automation.softwares || [],
      workflowSteps: getWorkflowSteps().map((step) => ({ ...step })),
    });
    setNewStepTitle('');
    setNewSoftware('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setNewStepTitle('');
    setNewSoftware('');
  };

  const handleSaveDetails = async () => {
    if (savingDetails) return;
    if (!editForm.name.trim()) {
      alert('Please enter an automation name.');
      return;
    }

    setSavingDetails(true);
    try {
      const orderedSteps = editForm.workflowSteps.map((step, index) => ({ ...step, order: index + 1 }));
      const patch = {
        name: editForm.name.trim(),
        description: editForm.description,
        category: editForm.category,
        softwares: editForm.softwares,
        workflowSteps: orderedSteps,
      };

      if (automation.isUserRequest) {
        await patchUserRequestDocs(patch);
      } else {
        await updateDoc(doc(db, 'automations', automationId), {
          ...patch,
          lastUpdated: new Date().toISOString(),
        });
        if (requestingUserId) {
          const updatedCustomizations = { ...userCustomizations, workflowSteps: orderedSteps };
          await saveUserAutomationCustomizations(requestingUserId, automationId, updatedCustomizations);
          setUserCustomizations(updatedCustomizations);
        }
      }

      setAutomation((prev) => ({ ...prev, ...patch }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving automation details:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingDetails(false);
    }
  };

  // ---- Workflow step editing (local until Save) ----
  const handleAddStep = () => {
    const title = newStepTitle.trim();
    if (!title) return;
    setEditForm((prev) => ({
      ...prev,
      workflowSteps: [
        ...prev.workflowSteps,
        { id: `step-${Date.now()}`, title, emoji: '', order: prev.workflowSteps.length + 1 },
      ],
    }));
    setNewStepTitle('');
  };

  const handleUpdateStepTitle = (index, title) => {
    setEditForm((prev) => ({
      ...prev,
      workflowSteps: prev.workflowSteps.map((step, i) => (i === index ? { ...step, title } : step)),
    }));
  };

  const handleDeleteStep = (index) => {
    setEditForm((prev) => ({
      ...prev,
      workflowSteps: prev.workflowSteps.filter((_, i) => i !== index),
    }));
  };

  const handleMoveStep = (index, direction) => {
    setEditForm((prev) => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.workflowSteps.length) return prev;
      const steps = [...prev.workflowSteps];
      [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
      return { ...prev, workflowSteps: steps };
    });
  };

  const handleToggleSoftware = (softwareId) => {
    setEditForm((prev) => ({
      ...prev,
      softwares: prev.softwares.includes(softwareId)
        ? prev.softwares.filter((s) => s !== softwareId)
        : [...prev.softwares, softwareId],
    }));
  };

  // ---- Emoji picking ----
  const pickEmoji = async (emoji) => {
    if (!emojiTarget) return;

    // Step emojis edit local state only; saved with the rest of the edit form
    if (emojiTarget.kind === 'step') {
      setEditForm((prev) => ({
        ...prev,
        workflowSteps: prev.workflowSteps.map((step, i) =>
          i === emojiTarget.index ? { ...step, emoji: emoji || '' } : step
        ),
      }));
      setEmojiTarget(null);
      return;
    }

    // Display emoji saves immediately (emojis[0] — portal cards read the first entry)
    setEmojiSaving(true);
    try {
      const emojis = emoji ? [emoji] : [];
      if (automation.isUserRequest) {
        await patchUserRequestDocs({ emojis });
      } else {
        await updateDoc(doc(db, 'automations', automationId), {
          emojis,
          lastUpdated: new Date().toISOString(),
        });
      }
      setAutomation((prev) => ({ ...prev, emojis }));
      setEmojiTarget(null);
    } catch (error) {
      console.error('Emoji save failed:', error);
    } finally {
      setEmojiSaving(false);
    }
  };

  // ---- Navigation and delete ----
  const handleBackNavigation = () => {
    if (location.state?.fromPortalView) {
      navigate(location.state?.returnPath || '/crm/portalview');
    } else {
      navigate('/crm/fulfillment');
    }
  };

  const handleDeleteRequest = async () => {
    if (!automation || deleting) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this automation request? This action cannot be undone.'
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      if (automation.isUserRequest) {
        // Remove from the account document (source of truth for PortalView)
        const finalAccountId = await resolveAccountId();
        if (finalAccountId) {
          try {
            const accountRef = doc(db, 'accounts', finalAccountId);
            const accountDoc = await getDoc(accountRef);
            if (accountDoc.exists()) {
              const accountRequests = accountDoc.data().userAutomationRequests || [];
              await updateDoc(accountRef, {
                userAutomationRequests: accountRequests.filter((req) => req.id !== automationId),
                lastUpdated: new Date().toISOString(),
              });
            }
          } catch (error) {
            console.error('Error deleting from account document:', error);
          }
        }

        // Remove from the user document
        if (requestingUserId) {
          try {
            const userRef = doc(db, 'users', requestingUserId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userRequests = userDoc.data().userAutomationRequests || [];
              await updateDoc(userRef, {
                userAutomationRequests: userRequests.filter((req) => req.id !== automationId),
                lastUpdated: new Date().toISOString(),
              });
            }
          } catch (error) {
            console.error('Error deleting from user document:', error);
          }
        }
      } else {
        // Template automation — delete the request pointing at it
        const requestsSnapshot = await getDocs(
          query(collection(db, 'automationRequests'), where('automationId', '==', automationId))
        );
        if (!requestsSnapshot.empty) {
          await deleteDoc(doc(db, 'automationRequests', requestsSnapshot.docs[0].id));
        }
      }

      handleBackNavigation();
    } catch (error) {
      console.error('Error deleting automation request:', error);
      alert('Failed to delete automation request. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading automation details...</p>
        </div>
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <Zap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Automation Not Found</h3>
          <p className="text-gray-500 dark:text-gray-400">The requested automation could not be found.</p>
          <button
            onClick={() => navigate('/crm/fulfillment')}
            className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
          >
            Back to Fulfillment
          </button>
        </div>
      </div>
    );
  }

  const displayEmoji = automation.emojis?.[0] || '';
  const displaySteps = isEditing ? editForm.workflowSteps : getWorkflowSteps();
  const displaySoftwares = isEditing ? editForm.softwares : automation.softwares || [];
  const recentActivity = [...(automation.statusHistory || [])]
    .filter((entry) => entry && entry.timestamp)
    .reverse()
    .slice(0, 8);
  const softwareTypeOptions = availableSoftwares.filter((software) => software.isType);

  return (
    <div className="min-h-screen p-6 relative">
      {/* Header — back arrow, centered emoji + title + status, actions on one row */}
      <PortalBackground />
      <div className="relative">
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackNavigation}
            title="Back"
            className={`shrink-0 p-2 rounded-full transition-colors ${
              darkMode
                ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <ArrowLeft size={22} />
          </button>

          <div className="flex-1 text-center min-w-0">
            <div className="flex items-center justify-center gap-3">
              {/* Display emoji — click to pick with the shared emoji picker */}
              <button
                type="button"
                onClick={() => setEmojiTarget({ kind: 'display' })}
                title="Choose the display emoji"
                className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {displayEmoji ? (
                  <span className="text-2xl leading-none">{displayEmoji}</span>
                ) : (
                  <SmilePlus className={`w-5 h-5 ${subtle}`} />
                )}
              </button>
              {isEditing ? (
                <input
                  type="text"
                  className={`text-2xl font-bold border-b ${darkMode ? 'border-gray-600' : 'border-gray-300'} focus:border-purple-500 focus:outline-none text-center bg-transparent ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  value={editForm.name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              ) : (
                <h1 className={`text-2xl font-bold tracking-tight truncate text-gray-900 dark:text-white`}>
                  {automation.name || 'Untitled Automation'}
                </h1>
              )}
              {getStatusBadge(automation.status)}
            </div>
            <p className={`text-sm mt-1 ${subtle}`}>Technology Fulfillment • {automation.company}</p>
          </div>

          <div className="flex space-x-2 shrink-0">
            {isEditing ? (
              <>
                <button
                  onClick={handleSaveDetails}
                  disabled={savingDetails}
                  className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Check size={16} className="mr-1" />
                  {savingDetails ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cancelEditing}
                  disabled={savingDetails}
                  className={`px-3 py-1 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} rounded-md flex items-center`}
                >
                  <X size={16} className="mr-1" />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={startEditing}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Edit size={16} className="mr-1" />
                  Edit
                </button>
                <button
                  onClick={handleDeleteRequest}
                  disabled={deleting}
                  className={`p-2 rounded-md text-white ${
                    deleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
                  }`}
                  title="Delete Automation Request"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className={panelCard}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Description
              </h2>
              {isEditing ? (
                <textarea
                  className={inputClass}
                  rows={4}
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              ) : (
                <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {automation.description || 'No description provided'}
                </p>
              )}
            </div>

            {/* Workflow Steps — title-only rows, each with its own emoji */}
            <div className={panelCard}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Workflow Steps
              </h2>

              <div className="space-y-2">
                {displaySteps.map((step, index) => (
                  <div
                    key={step.id || index}
                    className={`flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}
                  >
                    {isEditing ? (
                      <button
                        type="button"
                        onClick={() => setEmojiTarget({ kind: 'step', index })}
                        title="Choose an emoji for this step"
                        className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center transition-colors ${
                          darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-white hover:bg-gray-200 border border-gray-200'
                        }`}
                      >
                        {step.emoji ? (
                          <span className="text-lg leading-none">{step.emoji}</span>
                        ) : (
                          <SmilePlus className={`w-4 h-4 ${subtle}`} />
                        )}
                      </button>
                    ) : (
                      step.emoji && <span className="shrink-0 text-xl leading-none">{step.emoji}</span>
                    )}
                    <span className={`shrink-0 text-xs font-semibold w-5 text-center ${subtle}`}>{index + 1}.</span>
                    {isEditing ? (
                      <input
                        type="text"
                        className={`flex-1 min-w-0 rounded-md border px-3 py-1.5 text-sm ${
                          darkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        value={step.title}
                        onChange={(e) => handleUpdateStepTitle(index, e.target.value)}
                      />
                    ) : (
                      <span className={`flex-1 min-w-0 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {step.title}
                      </span>
                    )}
                    {isEditing && (
                      <div className="flex items-center shrink-0">
                        <button
                          onClick={() => handleMoveStep(index, 'up')}
                          disabled={index === 0}
                          className={`p-1 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`}
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveStep(index, 'down')}
                          disabled={index === displaySteps.length - 1}
                          className={`p-1 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`}
                          title="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteStep(index)}
                          className="p-1 text-red-400 hover:text-red-300"
                          title="Delete step"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {displaySteps.length === 0 && (
                  <p className={`text-sm text-center py-6 ${subtle}`}>No workflow steps yet</p>
                )}

                {isEditing && (
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="text"
                      value={newStepTitle}
                      onChange={(e) => setNewStepTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddStep();
                        }
                      }}
                      placeholder="Step title"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={handleAddStep}
                      disabled={!newStepTitle.trim()}
                      className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <Plus size={16} /> Add step
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Request Details */}
            <div className={panelCard}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Request Details
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Building2 className={`w-4 h-4 shrink-0 ${subtle}`} />
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{automation.company}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className={`w-4 h-4 shrink-0 ${subtle}`} />
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{automation.requestedByName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className={`w-4 h-4 shrink-0 ${subtle}`} />
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{formatDate(automation.requestDate)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag className={`w-4 h-4 shrink-0 ${subtle}`} />
                  {isEditing ? (
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, category: e.target.value }))}
                      className={`flex-1 rounded-md border px-2 py-1 text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="general">General</option>
                      <option value="lead">Lead</option>
                      <option value="design">Design</option>
                      <option value="build">Build</option>
                      <option value="office">Office</option>
                    </select>
                  ) : (
                    <span className={`capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {automation.category || 'General'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Status management */}
            <div className={panelCard}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Status
              </h2>
              <select
                value={selectedStatus || 'requested'}
                onChange={(e) => setSelectedStatus(e.target.value)}
                disabled={!requestingUserId || savingStatus}
                className={`${inputClass} mb-3`}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
              <button
                onClick={handleSaveStatus}
                disabled={!requestingUserId || savingStatus || selectedStatus === automation.status}
                className="w-full px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {savingStatus ? 'Saving...' : 'Update Status'}
              </button>
              {!requestingUserId && (
                <p className={`text-xs text-center mt-2 ${subtle}`}>
                  No portal user is linked to this request, so the status can't be saved.
                </p>
              )}
            </div>

            {/* Software Integration */}
            <div className={panelCard}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Software Integration
              </h2>

              {isEditing && (
                <div className="mb-4 flex items-center gap-2">
                  <select
                    value={newSoftware}
                    onChange={(e) => setNewSoftware(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select software type...</option>
                    {softwareTypeOptions
                      .filter((softwareType) => !editForm.softwares.includes(softwareType.id))
                      .map((softwareType) => (
                        <option key={softwareType.id} value={softwareType.id}>
                          {softwareType.name} ({softwareType.tier})
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => {
                      if (newSoftware) {
                        handleToggleSoftware(newSoftware);
                        setNewSoftware('');
                      }
                    }}
                    disabled={!newSoftware}
                    className="shrink-0 inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              )}

              <div className="space-y-2">
                {displaySoftwares.map((softwareTypeId) => {
                  const displayName = getSoftwareDisplayName(softwareTypeId);
                  const logo = getUserSoftwareLogo(softwareTypeId);
                  const connected = isSoftwareConnected(softwareTypeId);
                  return (
                    <div
                      key={softwareTypeId}
                      className={`flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`shrink-0 h-9 w-9 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center`}>
                          {logo ? (
                            <img src={logo} alt={`${displayName} logo`} className="h-5 w-auto object-contain" />
                          ) : (
                            <Zap className={`h-4 w-4 ${connected ? 'text-green-500' : subtle}`} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {displayName}
                          </p>
                          <p className={`text-[11px] ${connected ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                            {connected ? 'Connected' : 'Not connected'}
                          </p>
                        </div>
                      </div>
                      {isEditing ? (
                        <button
                          onClick={() => handleToggleSoftware(softwareTypeId)}
                          className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      ) : (
                        requestingUserId && (
                          <button
                            onClick={() => handleUpdateConnectionStatus(softwareTypeId, !connected)}
                            disabled={savingConnectionStatus}
                            className={`shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                              connected
                                ? darkMode
                                  ? 'border border-gray-600 text-gray-300 hover:bg-gray-600'
                                  : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                            }`}
                            title={connected ? 'Mark this software as not connected' : 'Mark this software as connected'}
                          >
                            {connected ? 'Disconnect' : 'Mark connected'}
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
                {displaySoftwares.length === 0 && (
                  <p className={`text-sm text-center py-6 ${subtle}`}>No software types added yet</p>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <div className={panelCard}>
                <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Recent Activity
                </h2>
                <div className="space-y-2">
                  {recentActivity.map((entry, index) => (
                    <div
                      key={`${entry.timestamp}-${index}`}
                      className={`flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}
                    >
                      {getStatusBadge(entry.status)}
                      <span className={`text-xs text-right ${subtle}`}>{formatDate(entry.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Emoji picker (display emoji and step emojis) */}
      <Modal isOpen={!!emojiTarget} onClose={() => !emojiSaving && setEmojiTarget(null)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an emoji
        </h2>
        <p className={`text-sm mb-4 text-center ${subtle}`}>
          {emojiTarget?.kind === 'step'
            ? editForm.workflowSteps[emojiTarget.index]?.title || 'Step'
            : `${automation.name} — display emoji`}
        </p>
        <div className="flex justify-center">
          {emojiTarget && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => !emojiSaving && pickEmoji(emojiData.emoji)}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {(emojiTarget?.kind === 'step'
            ? !!editForm.workflowSteps[emojiTarget.index]?.emoji
            : !!displayEmoji) ? (
            <button
              onClick={() => pickEmoji('')}
              disabled={emojiSaving}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Remove emoji
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setEmojiTarget(null)}
            disabled={emojiSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {emojiSaving ? 'Saving…' : 'Cancel'}
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default AutomationDetail;
